import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBars, FaTimes, FaCloud, FaHome, FaBoxOpen, FaUsers, FaEnvelope } from 'react-icons/fa';

const accent = '#7ec8ff';
const barBg = 'rgba(3, 26, 56, 0.92)';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${barBg};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.05rem;
  font-weight: 800;
  color: #ffffff;
  text-decoration: none;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  flex-shrink: 0;

  &:hover {
    color: ${accent};
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

const NavLinks = styled.div`
  margin-left: auto;
  display: flex;
  gap: 0.25rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.78);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.5rem 0.75rem;
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  margin-left: auto;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.4rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #031a38;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  z-index: 1001;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 1rem 1.25rem;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.45);
    background: rgba(255, 255, 255, 0.06);
    color: ${accent};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  const handleNavClick = (e, path) => {
    e.preventDefault();
    if (path.startsWith('#')) {
      scrollToSection(path.substring(1));
    } else {
      closeMobileMenu();
    }
  };

  return (
    <HeaderContainer>
      <Nav>
        <Logo to="/" onClick={(e) => handleNavClick(e, '#home')}>
          Энткор-е
        </Logo>

        <NavLinks>
          <NavLink to="/" onClick={(e) => handleNavClick(e, '#home')}>
            <FaHome /> Главная
          </NavLink>
          <NavLink to="/" onClick={(e) => handleNavClick(e, '#products')}>
            <FaBoxOpen /> Услуги
          </NavLink>
          <NavLink to="/" onClick={(e) => handleNavClick(e, '#about')}>
            <FaUsers /> О нас
          </NavLink>
          <NavLink to="/" onClick={(e) => handleNavClick(e, '#contact')}>
            <FaEnvelope /> Контакты
          </NavLink>
          <NavLink to="/product/e-node-cloud">
            <FaCloud /> e-node оазис (cloud)
          </NavLink>
        </NavLinks>

        <MobileMenuButton type="button" onClick={toggleMobileMenu} aria-label="Открыть меню">
          <FaBars />
        </MobileMenuButton>
      </Nav>

      {isMobileMenuOpen && (
        <MobileMenu
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <CloseButton type="button" onClick={closeMobileMenu} aria-label="Закрыть меню">
            <FaTimes />
          </CloseButton>

          <MobileNavLink to="/" onClick={(e) => handleNavClick(e, '#home')}>
            <FaHome /> Главная
          </MobileNavLink>
          <MobileNavLink to="/" onClick={(e) => handleNavClick(e, '#products')}>
            <FaBoxOpen /> Услуги
          </MobileNavLink>
          <MobileNavLink to="/" onClick={(e) => handleNavClick(e, '#about')}>
            <FaUsers /> О нас
          </MobileNavLink>
          <MobileNavLink to="/" onClick={(e) => handleNavClick(e, '#contact')}>
            <FaEnvelope /> Контакты
          </MobileNavLink>
          <MobileNavLink to="/product/e-node-cloud" onClick={closeMobileMenu}>
            <FaCloud /> e-node оазис (cloud)
          </MobileNavLink>
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

export default Header;
